import React, { useState, useEffect, useCallback } from 'react';
import {
  TextInput,
  Form,
  Button,
  InlineLoading,
  TextArea,
  Link,
  Toggle,
  RadioButtonGroup,
  RadioButton,
  DatePicker,
  DatePickerInput,
  Dropdown,
  MultiSelect,
  TooltipDefinition,
  TimePicker,
  TimePickerSelect,
  SelectItem
} from 'carbon-components-react';
import {
  Information16,
  WarningAlt16,
  Help16,
  CheckmarkOutline16,
  FaceSatisfied16,
  FaceDissatisfied16,
  EventSchedule16,
  Coronavirus16,
  Bullhorn16,
  Pin16,
  Tools16,
  Tag16,
  Idea16,
  Clean16
} from '@carbon/icons-react';
import _ from 'lodash';
import { FormError } from 'components';
import Utils, { getNewDate, toLocalDateFormat } from 'utilities/helper.util';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchMemberStart,
  selectedMembers,
  selectMemberLoading,
  clearSelectedMember
} from 'redux/members';
import SearchOrganisations from './searchOrganisations';

const AddAnnouncementForm: React.FC<any> = ({
  formik,
  isSubmittingForm,
  isSuccess,
  handleCancel,
  editId,
  editData
}: any) => {
  const dispatch: any = useDispatch();
  const organisations: Array<any> = useSelector(selectedMembers);
  const isLoadingOrganisations: boolean = useSelector(selectMemberLoading);
  const [
    selectedAnnouncementType,
    setSelectedAnnouncementType
  ] = useState<string>(editData.noticeType);
  const [organisationList, setOrganisationList] = useState<Array<any>>([]);
  const [showActionButton, setShowActionButton] = useState<boolean>(
    editData.callToActionLabel ? true : false
  );
  const [selectedIcon, setSelectedIcon] = useState<string>('');
  const [selectedPublishType, setSelectedPublishType] = useState<string>(
    editData.startAt ? '2' : '1'
  );
  const [isAbleToHide, setIsAbleToHide] = useState<boolean>(
    editData.isUserAbleToHide ? true : false
  );
  const [selectedCategory, setSelectedCategory] = useState<string>('IMPORTANT');
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(true);
  const [selectedOrgs, setSelectedOrgs] = useState<Array<any>>(
    editData?.Organisations?.map((org: any) => {
      return {
        value: org.id,
        label: `${org.name} - ${org.utilityId}`
      };
    }) || []
  );

  const isEditMode = editId !== 0;
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    isSubmitting
  } = formik;

  let isAddEnabled = false;

  if (values.noticeType === 'GENERAL') {
    isAddEnabled =
      values.title &&
      values.text &&
      values.category &&
      values.startAt &&
      values.endAt &&
      values.icon &&
      values.targetRoles &&
      Object.keys(errors).length <= 0;
  } else if (values.noticeType === 'MEMBER') {
    isAddEnabled =
      values.title &&
      values.text &&
      values.startAt &&
      values.endAt &&
      values.targetOrganisations &&
      Object.keys(errors).length <= 0;
  }

  const categoryListItems = [
    {
      value: 'IMPORTANT',
      label: 'Important'
    },
    {
      value: 'NOTICE',
      label: 'Notice'
    },
    {
      value: 'INFORMATION',
      label: 'Information'
    }
  ];

  const rolesListItems = [
    {
      value: 'ENQUIRER',
      label: 'Enquirer'
    },
    {
      value: 'DBYDADMIN',
      label: 'BYDA'
    },
    {
      value: 'AOADMIN',
      label: 'Member'
    },
    {
      value: 'SYSADMIN',
      label: 'Support'
    },
    {
      value: 'UNAUTHENTICATED',
      label: 'Unauthenticated'
    }
  ];

  let startAtMin = toLocalDateFormat(new Date());
  if (isEditMode) {
    const originalStartAt = getNewDate(editData.startAt).startOf('day');
    const todayStartAt = getNewDate().startOf('day');
    if (todayStartAt > originalStartAt) {
      startAtMin = toLocalDateFormat(originalStartAt);
    }
  }
  const startAtMax = values.endAt ? toLocalDateFormat(values.endAt) : '';
  const endAtMin = values.startAt
    ? toLocalDateFormat(values.startAt)
    : toLocalDateFormat(new Date());

  let buttonSaveWording = '';
  if (isEditMode) {
    buttonSaveWording = 'Save changes';
  } else {
    buttonSaveWording =
      selectedPublishType === '1' ? 'Publish now' : 'Schedule';
  }

  const handleToggleActionButton = (toggle: boolean) => {
    setShowActionButton(toggle);
  };

  const handleToggleHideOption = (toggle: boolean) => {
    setIsAbleToHide(toggle);
  };

  const handleOnClickIcon = useCallback(
    (iconId: string) => {
      if (selectedIcon) {
        const element = document.getElementById(selectedIcon);
        element?.classList.remove('selected');
      }
      const element = document.getElementById(iconId);
      element?.classList.add('selected');
      setFieldValue('icon', iconId);
      setSelectedIcon(iconId);
    },
    [selectedIcon, setFieldValue]
  );

  const handleAnnoucementTypeChange = (val: any) => {
    if (values.noticeType && values.targetOrganisations) {
      setFieldValue('targetOrganisations', '', false);
    }

    setFieldValue('noticeType', val);
    setFieldTouched('noticeType', true, true);
    setFieldValue('title', '', false);
    setFieldValue('text', '', false);

    setOrganisationList([]);
    setSelectedAnnouncementType(val);
    clearOrganisations();
    setSearchInputValue('');
  };

  const getSearchOrganisations = useCallback(
    Utils.debounce(async (args: Array<any>) => {
      const searchInputValue = args[0];
      const queryParams: any = {
        fields: 'id,name,utilityId',
        order: 'name',
        limit: 200,
        offset: 0,
        filter: `role:ASSETOWNER,name:"%${searchInputValue}%"`
      };
      searchInputValue?.length >= 2 && dispatch(fetchMemberStart(queryParams));
    }, 600),
    []
  );

  const handlePublishTypeChange = (val: any) => {
    setSelectedPublishType(val);
  };

  const handleOrganisationChange = ({
    selectedItems
  }: {
    selectedItems: any[];
  }) => {
    const values = selectedItems.map((i) => i.value);
    setFieldValue('targetOrganisations', values);
    setFieldTouched('targetOrganisations', true, true);

    let orgListToUse = organisationList;
    if (organisationList.length <= 0) {
      orgListToUse = organisationListStored;
    }

    const newlySelectedOrgs = orgListToUse.filter((org) =>
      values.includes(org.value)
    );
    const selectedOrgsNewArray: Array<any> = [...newlySelectedOrgs];

    setSelectedOrgs(selectedOrgsNewArray);
  };

  const handleTargetRolesChange = ({
    selectedItems
  }: {
    selectedItems: any[];
  }) => {
    const values = selectedItems.map((i) => i.value);
    setFieldValue('targetRoles', values);
    setFieldTouched('targetRoles', true, true);
  };

  const get24HourTimeFormat = (time: string, meridiem: string) => {
    const timeSplit = time.split(':');
    const hour = timeSplit[0];
    let hourInt = parseInt(hour);
    if (timeSplit?.length === 2) {
      if (meridiem === 'PM' && hourInt < 12) {
        hourInt = hourInt + 12;
      } else if (meridiem === 'AM' && hourInt === 12) {
        hourInt = 0;
      }
      return `${hourInt}:${timeSplit[1]}`;
    } else {
      return time;
    }
  };

  const handleDateTimeChange = (key: string, val: any) => {
    const {
      startAt,
      startAtTime,
      startAtAMPM,
      endAt,
      endAtTime,
      endAtAMPM
    } = values;

    const seconds = ':59';
    let newEndAtDateTime = '';
    let newStartAtDateTime = '';

    if (key === 'startAt') {
      if (startAtTime && startAtAMPM) {
        const new24HTimeFormat = get24HourTimeFormat(startAtTime, startAtAMPM);
        newStartAtDateTime = getNewDate(`${val} ${new24HTimeFormat}`)
          .utc()
          .format();
      }
    } else if (key === 'startAtTime') {
      if (startAt && startAtAMPM) {
        const new24HTimeFormat = get24HourTimeFormat(val, startAtAMPM);
        newStartAtDateTime = getNewDate(`${startAt} ${new24HTimeFormat}`)
          .utc()
          .format();
      }
    } else if (key === 'startAtAMPM') {
      if (startAt && startAtTime) {
        const new24HTimeFormat = get24HourTimeFormat(startAtTime, val);
        newStartAtDateTime = getNewDate(`${startAt} ${new24HTimeFormat}`)
          .utc()
          .format();
      }
    } else if (key === 'endAt') {
      if (endAtTime && endAtAMPM) {
        const new24HTimeFormat = get24HourTimeFormat(endAtTime, endAtAMPM);
        newEndAtDateTime = getNewDate(`${val} ${new24HTimeFormat}${seconds}`)
          .utc()
          .format();
      }
    } else if (key === 'endAtTime') {
      if (endAt && endAtAMPM) {
        const new24HTimeFormat = get24HourTimeFormat(val, endAtAMPM);
        newEndAtDateTime = getNewDate(`${endAt} ${new24HTimeFormat}${seconds}`)
          .utc()
          .format();
      }
    } else if (key === 'endAtAMPM') {
      if (endAt && endAtTime) {
        const new24HTimeFormat = get24HourTimeFormat(endAtTime, val);
        newEndAtDateTime = getNewDate(`${endAt} ${new24HTimeFormat}${seconds}`)
          .utc()
          .format();
      }
    }

    if (newStartAtDateTime) {
      setFieldValue('startAtDateTime', newStartAtDateTime, false);
    }
    if (newEndAtDateTime) {
      setFieldValue('endAtDateTime', newEndAtDateTime, false);
    }
  };

  const onChangeDate = (key: string, val: any, setField?: boolean) => {
    const newDate = getNewDate(val[0]).format('YYYY-MM-DD');
    setField && setFieldValue(key, newDate, true);
    handleDateTimeChange(key, newDate);
  };

  const onChangeTime = (key: string, val: any, setField?: boolean) => {
    setField && setFieldValue(key, val, true);
    handleDateTimeChange(key, val);
  };

  const getInitialCategoryValue = (value: string) => {
    if (value) {
      return categoryListItems.find((o) => value.includes(o.value));
    }
    return categoryListItems[0];
  };

  const getOrganisationOptionItem = (value: number[]) => {
    if (value) {
      const listToUse =
        editData && Object.keys(editData).length !== 0 && isPreviewMode
          ? organisationListStored
          : organisationList;
      const matched = listToUse.filter((o: any) => value.includes(o.value));
      return matched;
    }
    return [];
  };

  const getTargetRolesOptionItem = (value: string[]) => {
    if (value) {
      return rolesListItems.filter((o) => value.includes(o.value));
    }
    return [];
  };

  const clearOrganisations = useCallback(() => {
    dispatch(clearSelectedMember());
  }, [dispatch]);

  const onClickCancel = () => {
    clearOrganisations();
    handleCancel();
  };

  useEffect(() => {
    setFieldValue('toggleActionButton', showActionButton);
    if (!showActionButton) {
      setFieldValue('callToActionLabel', '');
      setFieldValue('callToActionLink', '');
    }
  }, [showActionButton, setFieldValue]);

  useEffect(() => {
    if (selectedPublishType === '1') {
      const todayDate = getNewDate().format('YYYY-MM-DD');
      setFieldValue(
        'startAt',
        isEditMode || values.startAt ? values.startAt || todayDate : todayDate
      );
      const todayStartAtDateTime = getNewDate().startOf('day').utc().format();
      setFieldValue('startAtDateTime', todayStartAtDateTime);
    } else if (selectedPublishType === '2') {
      setFieldValue(
        'startAt',
        isEditMode || values.startAt ? values.startAt || '' : ''
      );
    }
  }, [selectedPublishType, setFieldValue, isEditMode, values.startAt]);

  useEffect(() => {
    setFieldValue('isUserAbleToHide', isAbleToHide);
  }, [isAbleToHide, setFieldValue]);

  useEffect(() => {
    setFieldValue('category', selectedCategory);
  }, [selectedCategory, setFieldValue]);

  useEffect(() => {
    if (isEditMode) {
      handleOnClickIcon(values.icon);
    }
  }, [isEditMode, values.icon, handleOnClickIcon]);

  useEffect(() => {
    if (organisations && !isLoadingOrganisations) {
      const orgList: Array<any> = [];

      organisations.forEach((org) => {
        if (org.utilityId !== 0) {
          const newOrgObject = {
            value: org.id,
            label: `${org.name} - ${org.utilityId}`
          };
          orgList.push(newOrgObject);
        }
      });
      if (selectedOrgs.length > 0) {
        orgList.push(...selectedOrgs);
      }

      const filteredOrgList = _.uniqBy(orgList, 'label');
      setOrganisationList(filteredOrgList);
    }
  }, [organisations, isLoadingOrganisations, selectedOrgs]);

  useEffect(() => {
    if (isSubmitting) {
      clearOrganisations();
    }
  }, [isSubmitting, clearOrganisations]);

  useEffect(() => {
    if (values.startAtDateTime) {
      setFieldTouched('startAtDateTime', true, true);
    }
    if (values.endAtDateTime) {
      setFieldTouched('endAtDateTime', true, true);
    }
  }, [values.startAtDateTime, values.endAtDateTime, setFieldTouched]);

  const organisationListStored = editData?.Organisations?.map((org: any) => {
    return {
      value: org.id,
      label: `${org.name} - ${org.utilityId}`
    };
  });

  return (
    <Form
      className="announcement-form"
      data-testid="announcement-form"
      onSubmit={handleSubmit}
    >
      <div className="add-announcement-form-header">
        <div>
          <h1 className="administrator-form-main-heading">Announcement</h1>
        </div>
        <div className="close">
          <Link onClick={onClickCancel}>Close</Link>
        </div>
      </div>

      <div className="divider"></div>
      <div className="add-announcement-form-body">
        <div className="add-announcenment-form-item announcement-type-section">
          <div className="announcement-type-title">
            <TooltipDefinition
              tooltipText={
                <div>
                  <p>
                    <span className="bold-style">General</span> announcements
                    are displayed when a user signs in to the service.
                  </p>
                  <br />
                  <p>
                    <span className="bold-style">Member</span> specific
                    announcements are shown when a user lodges an enquiry in the
                    member&apos;s AOI.
                  </p>
                </div>
              }
              direction={'bottom'}
              className="announcement-type-tooltip"
            >
              Announcement type
            </TooltipDefinition>
          </div>
          <RadioButtonGroup
            name="noticeType"
            defaultSelected={selectedAnnouncementType}
            onChange={handleAnnoucementTypeChange}
          >
            <RadioButton labelText="General" value="GENERAL" id="noticeType1" />
            <RadioButton labelText="Member" value="MEMBER" id="noticeType2" />
          </RadioButtonGroup>
        </div>
        <div className="divider"></div>
        {selectedAnnouncementType ? (
          <>
            {selectedAnnouncementType === 'MEMBER' && (
              <>
                <div className="add-announcenment-form-item">
                  <div className="search-organisation-section">
                    <div className="search-organisation-field-title">
                      Prefilter organisations
                    </div>
                    <SearchOrganisations
                      setSearchInputValue={setSearchInputValue}
                      searchInputValue={searchInputValue}
                      getSearchOrganisations={getSearchOrganisations}
                      clearOrganisations={clearOrganisations}
                      setIsPreviewMode={setIsPreviewMode}
                    />
                  </div>
                  <MultiSelect
                    id="targetOrganisations"
                    items={
                      editData &&
                      Object.keys(editData).length !== 0 &&
                      isPreviewMode
                        ? organisationListStored
                        : organisationList
                    }
                    disabled={
                      editData &&
                      Object.keys(editData).length !== 0 &&
                      isPreviewMode
                        ? false
                        : organisationList.length <= 0
                    }
                    label={
                      values.targetOrganisations &&
                      values.targetOrganisations.length > 0
                        ? 'Organisation'
                        : 'Choose options'
                    }
                    titleText="Organisation"
                    onChange={handleOrganisationChange}
                    initialSelectedItems={getOrganisationOptionItem(
                      values.targetOrganisations
                    )}
                  />
                  {errors.targetOrganisations &&
                    touched.targetOrganisations && (
                      <FormError>{errors.targetOrganisations}</FormError>
                    )}
                </div>
                <div className="divider"></div>
              </>
            )}
            <div className="add-announcenment-form-item">
              <TextInput
                id="title"
                name="title"
                labelText="Title"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.title}
                data-testid="title"
                maxLength={50}
              />
              {touched.title && errors.title ? (
                <FormError>{errors.title}</FormError>
              ) : null}
            </div>

            <div className="add-announcenment-form-item">
              <TextArea
                id="text"
                name="text"
                labelText="Content"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.text}
                data-testid="text"
                maxLength={250}
              />
              {touched.text && errors.text ? (
                <FormError>{errors.text}</FormError>
              ) : null}
            </div>

            {selectedAnnouncementType === 'GENERAL' && (
              <>
                <div className="add-announcenment-form-item toggle">
                  <div>
                    <span className="description">Include action button</span>
                  </div>
                  <div>
                    <Toggle
                      id={'toggleActionButton'}
                      className="changeToggle"
                      defaultToggled={showActionButton}
                      onToggle={(toggle) => handleToggleActionButton(toggle)}
                    />
                  </div>
                </div>

                {showActionButton && (
                  <>
                    <div className="add-announcenment-form-item">
                      <TextInput
                        id="callToActionLabel"
                        name="callToActionLabel"
                        labelText="Action button label"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.callToActionLabel}
                        data-testid="callToActionLabel"
                        maxLength={30}
                      />
                      {touched.callToActionLabel && errors.callToActionLabel ? (
                        <FormError>{errors.callToActionLabel}</FormError>
                      ) : null}
                    </div>

                    <div className="add-announcenment-form-item">
                      <TextInput
                        id="callToActionLink"
                        name="callToActionLink"
                        labelText="Action button URL"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.callToActionLink}
                        data-testid="callToActionLink"
                        maxLength={250}
                      />
                      {touched.callToActionLink && errors.callToActionLink ? (
                        <FormError>{errors.callToActionLink}</FormError>
                      ) : null}
                    </div>
                  </>
                )}

                <div className="divider"></div>

                <div className="add-announcenment-form-item">
                  <div className="iconSectionTitle">
                    <span>Announcement icon</span>
                  </div>
                  <div className="iconContainer">
                    <div
                      className="iconBox"
                      id="Information"
                      onClick={() => handleOnClickIcon('Information')}
                    >
                      <Information16 />
                    </div>
                    <div
                      className="iconBox"
                      id="WarningAlt"
                      onClick={() => handleOnClickIcon('WarningAlt')}
                    >
                      <WarningAlt16 />
                    </div>
                    <div
                      className="iconBox"
                      id="Help"
                      onClick={() => handleOnClickIcon('Help')}
                    >
                      <Help16 />
                    </div>
                    <div
                      className="iconBox"
                      id="CheckmarkOutline"
                      onClick={() => handleOnClickIcon('CheckmarkOutline')}
                    >
                      <CheckmarkOutline16 />
                    </div>
                    <div
                      className="iconBox"
                      id="FaceSatisfied"
                      onClick={() => handleOnClickIcon('FaceSatisfied')}
                    >
                      <FaceSatisfied16 />
                    </div>
                    <div
                      className="iconBox"
                      id="FaceDissatisfied"
                      onClick={() => handleOnClickIcon('FaceDissatisfied')}
                    >
                      <FaceDissatisfied16 />
                    </div>
                    <div
                      className="iconBox"
                      id="EventSchedule"
                      onClick={() => handleOnClickIcon('EventSchedule')}
                    >
                      <EventSchedule16 />
                    </div>
                    <div
                      className="iconBox"
                      id="Coronavirus"
                      onClick={() => handleOnClickIcon('Coronavirus')}
                    >
                      <Coronavirus16 />
                    </div>
                    <div
                      className="iconBox"
                      id="Bullhorn"
                      onClick={() => handleOnClickIcon('Bullhorn')}
                    >
                      <Bullhorn16 />
                    </div>
                    <div
                      className="iconBox"
                      id="Pin"
                      onClick={() => handleOnClickIcon('Pin')}
                    >
                      <Pin16 />
                    </div>
                    <div
                      className="iconBox"
                      id="Tools"
                      onClick={() => handleOnClickIcon('Tools')}
                    >
                      <Tools16 />
                    </div>
                    <div
                      className="iconBox"
                      id="Tag"
                      onClick={() => handleOnClickIcon('Tag')}
                    >
                      <Tag16 />
                    </div>
                    <div
                      className="iconBox"
                      id="Idea"
                      onClick={() => handleOnClickIcon('Idea')}
                    >
                      <Idea16 />
                    </div>
                    <div
                      className="iconBox"
                      id="Clean"
                      onClick={() => handleOnClickIcon('Clean')}
                    >
                      <Clean16 />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="divider"></div>

            <div className="add-announcenment-form-item">
              <RadioButtonGroup
                name="publish-selection"
                defaultSelected={selectedPublishType}
                orientation="vertical"
                onChange={handlePublishTypeChange}
              >
                <RadioButton
                  labelText="Publish immediately"
                  value="1"
                  id="radio-1"
                />
                <RadioButton
                  labelText="Scheduled publication"
                  value="2"
                  id="radio-2"
                />
              </RadioButtonGroup>
            </div>

            {selectedPublishType === '2' && (
              <div className="add-announcenment-form-item">
                <div className="date-and-time-picker-container">
                  <DatePicker
                    dateFormat="d/m/Y"
                    datePickerType="single"
                    minDate={startAtMin}
                    maxDate={startAtMax}
                    onChange={(val: any) => {
                      onChangeDate('startAt', val, true);
                    }}
                    value={
                      values.startAt
                        ? Utils.toLocalDateFormat(values.startAt)
                        : ''
                    }
                  >
                    <DatePickerInput
                      id="startAt"
                      name="startAt"
                      data-testid="startAt"
                      labelText="Start date"
                      placeholder="dd/mm/yyyy"
                      type="text"
                      autoComplete="off"
                    />
                  </DatePicker>
                  <TimePicker
                    id="startAtTime"
                    name="startAtTime"
                    pattern="^(0?[1-9]|1[0-2]):[0-5][0-9]$"
                    placeholder="hh:mm"
                    className="apply-filter-form-time-picker-from"
                    onChange={(val: any) => {
                      onChangeTime('startAtTime', val.target.value, true);
                    }}
                    value={values.startAtTime}
                  >
                    <TimePickerSelect
                      id="startAtAMPM"
                      name="startAtAMPM"
                      labelText="time-format-picker-end"
                      value={values.startAtAMPM}
                      onChange={(val: any) => {
                        onChangeTime('startAtAMPM', val.target.value, true);
                      }}
                    >
                      <SelectItem value="AM" text="AM" />
                      <SelectItem value="PM" text="PM" />
                    </TimePickerSelect>
                  </TimePicker>
                  {errors.startAtDateTime ? (
                    <FormError>{errors.startAtDateTime}</FormError>
                  ) : null}
                </div>
              </div>
            )}

            <div className="add-announcenment-form-item">
              <div className="date-and-time-picker-container">
                <DatePicker
                  dateFormat="d/m/Y"
                  datePickerType="single"
                  minDate={endAtMin}
                  onChange={(val: any) => {
                    onChangeDate('endAt', val, true);
                  }}
                  value={
                    values.endAt ? Utils.toLocalDateFormat(values.endAt) : ''
                  }
                >
                  <DatePickerInput
                    id="endAt"
                    name="endAt"
                    data-testid="endAt"
                    labelText="End date"
                    placeholder="dd/mm/yyyy"
                    type="text"
                    autoComplete="off"
                  />
                </DatePicker>
                <TimePicker
                  id="endAtTime"
                  name="endAtTime"
                  pattern="^(0?[1-9]|1[0-2]):[0-5][0-9]$"
                  placeholder="hh:mm"
                  className="apply-filter-form-time-picker-from"
                  onChange={(val: any) => {
                    onChangeTime('endAtTime', val.target.value, true);
                  }}
                  value={values.endAtTime}
                >
                  <TimePickerSelect
                    id="endAtAMPM"
                    name="endAtAMPM"
                    labelText="time-format-picker-end"
                    value={values.endAtAMPM}
                    onChange={(val: any) => {
                      onChangeTime('endAtAMPM', val.target.value, true);
                    }}
                  >
                    <SelectItem value="AM" text="AM" />
                    <SelectItem value="PM" text="PM" />
                  </TimePickerSelect>
                </TimePicker>
                {errors.endAtDateTime ? (
                  <FormError>{errors.endAtDateTime}</FormError>
                ) : null}
              </div>
            </div>

            {selectedAnnouncementType === 'GENERAL' && (
              <>
                <div className="divider"></div>

                <div className="add-announcenment-form-item">
                  <Dropdown
                    id="category"
                    titleText="Category"
                    direction="top"
                    label=""
                    items={categoryListItems}
                    itemToString={(item: any) => item.label}
                    onBlur={handleBlur}
                    initialSelectedItem={getInitialCategoryValue(
                      values.category
                    )}
                    onChange={(item: any) =>
                      setSelectedCategory(item?.selectedItem?.value)
                    }
                  />
                  {errors.category && touched.category && (
                    <FormError>{errors.category}</FormError>
                  )}
                </div>

                <div className="add-announcenment-form-item">
                  <MultiSelect
                    id="targetRoles"
                    direction="top"
                    items={rolesListItems}
                    label="Roles"
                    titleText="Roles (audience)"
                    onChange={handleTargetRolesChange}
                    initialSelectedItems={getTargetRolesOptionItem(
                      values.targetRoles
                    )}
                  />
                  {errors.targetRoles && touched.targetRoles && (
                    <FormError>{errors.targetRoles}</FormError>
                  )}
                </div>

                <div className="add-announcenment-form-item toggle last">
                  <div>
                    <TooltipDefinition
                      tooltipText={
                        'Turn this on to give users the option to hide the announcement'
                      }
                      direction={'top'}
                      className="description"
                    >
                      Show option to hide
                    </TooltipDefinition>
                  </div>
                  <div>
                    <Toggle
                      id={'toggleHideOption'}
                      className="changeToggle"
                      defaultToggled={values.isUserAbleToHide}
                      onToggle={(toggle) => handleToggleHideOption(toggle)}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="add-announcenment-form-item start-instruction">
            Choose an option to begin
          </div>
        )}
      </div>

      <section className="add-announcenment-form-item button">
        <div className="button-set">
          <Button
            id="cancel"
            name="cancel"
            kind="secondary"
            data-testid="cancel"
            onClick={onClickCancel}
          >
            Cancel
          </Button>
          <Button
            id="create"
            name="create"
            type="submit"
            kind="primary"
            data-testid="submit"
            size="sm"
            disabled={!isAddEnabled || isSubmittingForm}
          >
            {!isSubmittingForm ? (
              buttonSaveWording
            ) : (
              <InlineLoading
                description={isSuccess ? 'Saved' : 'Saving ...'}
                status={isSuccess ? 'finished' : 'active'}
                aria-live={'assertive'}
              />
            )}
          </Button>
        </div>
      </section>
    </Form>
  );
};

AddAnnouncementForm.displayName = 'AddAnnouncementForm';
export default AddAnnouncementForm;
